import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Hash, TrendingUp, Users, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import FashionCard from '../components/FashionCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAMPAIGN_HERO = "https://customer-assets.emergentagent.com/job_33bcf8e9-08ef-41f7-bae2-2f8d52c0ce23/artifacts/885zllgc_9207f5ef6c65062d4d90ac3878d73a956d130527cef9079cd23cbeae247c1a0d.png";

const CampaignLooksContent = ({ loading, campaignData, isAuthenticated }) => {
  if (loading) {
    return (
      <div className="bento-grid">
        {[1, 2, 3, 4].map((num) => (
          <div key={`skeleton-${num}`} className="skeleton rounded-sm" style={{ minHeight: '350px' }} />
        ))}
      </div>
    );
  }

  if (campaignData && campaignData.looks.length > 0) {
    return (
      <div className="bento-grid">
        {campaignData.looks.map((look, index) => (
          <FashionCard key={look.id} look={look} featured={index === 0} />
        ))}
      </div>
    );
  }

  return (
    <div className="text-center py-16">
      <Camera className="w-12 h-12 text-gray-600 mx-auto mb-4" />
      <p className="text-gray-500 text-lg mb-4">No looks yet. Be the first to contribute!</p>
      {isAuthenticated ? (
        <Link to="/create">
          <Button className="btn-luxury btn-luxury-accent">Share Your Look</Button>
        </Link>
      ) : (
        <Link to="/auth">
          <Button className="btn-luxury btn-luxury-secondary">Join to Participate</Button>
        </Link>
      )}
    </div>
  );
};

const RunwayToRealLifePage = () => {
  const { isAuthenticated } = useAuth();
  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCampaign = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/campaigns/runway-to-real-life`);
      setCampaignData(response.data);
    } catch {
      toast.error('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="campaign-page">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={CAMPAIGN_HERO} alt="Runway to Real Life" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#0a0a0a]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#C41E3A]/40 bg-[#C41E3A]/10 mb-8">
            <Hash className="w-4 h-4 text-[#C41E3A]" />
            <span className="text-sm uppercase tracking-[0.2em] text-[#C41E3A] font-medium">Active Campaign</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-serif italic text-white mb-6 animate-fade-in-up text-glow">
            #RunwayToRealLife
          </h1>
          <p className="text-lg md:text-xl text-gray-300 font-light tracking-wide mb-10 max-w-2xl mx-auto animate-fade-in-up stagger-1">
            Where the runway meets your wardrobe. Share how you translate high-fashion runway looks into your everyday style.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up stagger-2">
            {isAuthenticated ? (
              <Link to="/create?tags=RunwayToRealLife&brand=Louis+Vuitton">
                <Button className="btn-luxury btn-luxury-primary gap-2">
                  <Camera className="w-4 h-4" />
                  Submit Your Look
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button className="btn-luxury btn-luxury-primary gap-2">
                  Join & Participate
                </Button>
              </Link>
            )}
            <Link to="/feed">
              <Button className="btn-luxury btn-luxury-secondary gap-2">
                Explore Feed
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif italic text-white text-center mb-16">How It Works</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 border border-white/5 bg-[#121212] rounded-sm" data-testid="campaign-step-1">
              <div className="w-14 h-14 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/30 flex items-center justify-center mx-auto mb-6">
                <Camera className="w-6 h-6 text-[#C41E3A]" />
              </div>
              <h3 className="text-lg font-serif text-white mb-3">1. Capture Your Look</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Take a photo of your outfit inspired by a luxury runway collection. Show us your interpretation.
              </p>
            </div>

            <div className="text-center p-8 border border-white/5 bg-[#121212] rounded-sm" data-testid="campaign-step-2">
              <div className="w-14 h-14 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/30 flex items-center justify-center mx-auto mb-6">
                <Hash className="w-6 h-6 text-[#C41E3A]" />
              </div>
              <h3 className="text-lg font-serif text-white mb-3">2. Tag & Share</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Upload your look and add the #RunwayToRealLife tag. Tell us which collection inspired you.
              </p>
            </div>

            <div className="text-center p-8 border border-white/5 bg-[#121212] rounded-sm" data-testid="campaign-step-3">
              <div className="w-14 h-14 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/30 flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="w-6 h-6 text-[#C41E3A]" />
              </div>
              <h3 className="text-lg font-serif text-white mb-3">3. Get Featured</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Top looks get featured on our homepage and shared with our growing community of luxury fashion lovers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign Stats */}
      {campaignData && (
        <section className="py-16 px-6 md:px-12 bg-[#121212] border-t border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-3 gap-8 text-center">
              <div data-testid="campaign-stat-looks">
                <p className="text-4xl font-serif text-white">{campaignData.campaign.total_looks}</p>
                <p className="text-xs uppercase tracking-wider text-gray-500 mt-2">Looks Submitted</p>
              </div>
              <div data-testid="campaign-stat-brands">
                <p className="text-4xl font-serif text-white">5</p>
                <p className="text-xs uppercase tracking-wider text-gray-500 mt-2">Luxury Brands</p>
              </div>
              <div data-testid="campaign-stat-community">
                <p className="text-4xl font-serif text-white">
                  <Users className="w-8 h-8 inline-block text-[#C41E3A]" />
                </p>
                <p className="text-xs uppercase tracking-wider text-gray-500 mt-2">Growing Community</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Campaign Looks */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#C41E3A] mb-4">Featured Looks</p>
              <h2 className="text-3xl md:text-4xl font-serif italic text-white">
                #RunwayToRealLife Collection
              </h2>
            </div>
            <Link to="/feed" className="group flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
              <span className="text-sm uppercase tracking-wider">View All</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <CampaignLooksContent
            loading={loading}
            campaignData={campaignData}
            isAuthenticated={isAuthenticated}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#121212] border-t border-white/5 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-serif italic text-white mb-6">
            Ready to Join the Movement?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Show the world how you bring runway fashion into your real life.
          </p>
          {isAuthenticated ? (
            <Link to="/create?tags=RunwayToRealLife&brand=Louis+Vuitton">
              <Button className="btn-luxury btn-luxury-primary gap-2">
                <Camera className="w-4 h-4" />
                Submit Your Look
              </Button>
            </Link>
          ) : (
            <Link to="/auth" data-testid="campaign-cta-join">
              <Button className="btn-luxury btn-luxury-primary">
                Get Started
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

export default RunwayToRealLifePage;
